/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Detail Reducer
 */

import { SET_MOBILE } from '@zcode-ui/web-zumper-global-state'
import { alwaysReducer, payloadReducer } from '@zcode/reducers'
import type { Detail } from '@zcode/state-type'
import { combineReducers } from 'redux'
import { handleAction, handleActions } from 'redux-actions'
import {
  CLOSE_FLOORPLAN_MODAL,
  MOBILE_VIEW,
  OPEN_FLOORPLAN_MODAL,
  RECEIVE_ACTIVE_LISTINGS,
  RECEIVE_DIL_ADDRESSES,
  RECEIVE_DIL_BUILDINGS,
  RECEIVE_INACTIVE_LISTINGS,
  SET_IS_ADDRESS,
  SET_NEW_GALLERY_UX,
} from '../constants/index'
import { buildingReviews } from '../modules/buildingReviewsData/index'
import { cancellation } from '../modules/cancellation/index'
import { entity } from '../modules/entity/index'
import { lifestyles } from '../modules/lifestyles/index'
import { mlsData } from '../modules/mlsData/index'
import { monetizedSimilarListings } from '../modules/monetizedSimilarListings/index'
import { nearby } from '../modules/nearby/index'
import { prosAndCons } from '../modules/proConData/index'
import { ratings } from '../modules/ratings/index'
import { RECEIVE_LOCATION_SCHOOLS } from '../modules/schools/constants/index'
import { units } from '../modules/units/index'
import type { ActionTypes } from './types'

const DEFAULT_FLOORPLAN_MODAL_STATE = {
  isOpen: false,
  opener: null,
  selectedFloorplan: null,
} as const

export const detail = combineReducers<Detail, ActionTypes>({
  activeListings: handleAction<
    Detail['activeListings'],
    ActionTypes,
    typeof RECEIVE_ACTIVE_LISTINGS
  >(RECEIVE_ACTIVE_LISTINGS, payloadReducer, null),
  entity,
  cancellation,
  lifestyles,
  mobileView: handleActions<Detail['mobileView'], ActionTypes>(
    {
      [MOBILE_VIEW]: (state, { payload }) => !!payload,
      [SET_MOBILE]: (state, { payload }) => !!payload,
    },
    false
  ),
  monetizedSimilarListings,
  mlsData,
  nearby,
  prosAndCons,
  buildingReviews,
  units,
  inactiveListings: handleAction<
    Detail['inactiveListings'],
    ActionTypes,
    typeof RECEIVE_INACTIVE_LISTINGS
  >(RECEIVE_INACTIVE_LISTINGS, payloadReducer, null),
  dilAddresses: handleAction<Detail['dilAddresses'], ActionTypes, typeof RECEIVE_DIL_ADDRESSES>(
    RECEIVE_DIL_ADDRESSES,
    payloadReducer,
    null /* schema defaulted to ImList() */
  ),
  dilBuildings: handleAction<Detail['dilBuildings'], ActionTypes, typeof RECEIVE_DIL_BUILDINGS>(
    RECEIVE_DIL_BUILDINGS,
    payloadReducer,
    null /* schema defaulted to ImList() */
  ),
  floorplanModal: handleActions<Detail['floorplanModal'], ActionTypes>(
    {
      [CLOSE_FLOORPLAN_MODAL]: alwaysReducer(DEFAULT_FLOORPLAN_MODAL_STATE),
      [OPEN_FLOORPLAN_MODAL]: (state, { payload }) => ({
        ...payload,
        isOpen: true,
      }),
    },
    DEFAULT_FLOORPLAN_MODAL_STATE
  ),
  ratings,
  locationSchools: handleAction<
    Detail['locationSchools'],
    ActionTypes,
    typeof RECEIVE_LOCATION_SCHOOLS
  >(RECEIVE_LOCATION_SCHOOLS, payloadReducer, {}),
  isAddress: handleAction<Detail['isAddress'], ActionTypes, typeof SET_IS_ADDRESS>(
    SET_IS_ADDRESS,
    payloadReducer,
    false
  ),
  galleryUx: handleAction<Detail['galleryUx'], ActionTypes, typeof SET_NEW_GALLERY_UX>(
    SET_NEW_GALLERY_UX,
    payloadReducer,
    null
  ),
})
